import * as Application from 'expo-application';
import * as SecureStore from 'expo-secure-store';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { v5 as uuidv5 } from 'uuid';

export const STORAGE_KEY = 'installationID';

let installationID: string;

// Implementation inspired by https://github.com/expo/expo/blob/sdk-43/packages/expo/build/environment/getInstallationIdAsync.js
const UUID_NAMESPACE = '29cc8a0d-747c-5f85-9ff9-f2f16636d963';

const _getOrCreateInstallationID = async (): Promise<string> => {
  // https://github.com/expo/expo/blob/main/packages/expo-constants/CHANGELOG.md#931--2020-11-25
  if (Platform.OS === 'android') {
    const androidID = Application.getAndroidId();
    if (androidID) {
      return androidID;
    }
  }

  // Deprecated but use original installationID if available
  if (Constants.installationId) {
    return Constants.installationId;
  }

  // Generate an installationID based on application data
  const bundleIdentifier = Application.applicationId;
  const identifierForVendor =
    Platform.OS === 'ios' ? await Application.getIosIdForVendorAsync() : null;
  if (identifierForVendor) {
    return uuidv5(`${bundleIdentifier}-${identifierForVendor}`, UUID_NAMESPACE);
  }

  const installationTime = await Application.getInstallationTimeAsync();
  return uuidv5(
    `${bundleIdentifier}-${installationTime.getTime()}`,
    UUID_NAMESPACE,
  );
};

export const getInstallationID = async (): Promise<string> => {
  if (!installationID) {
    try {
      // Read installationID from SecureStore if it's available on device. Note
      // that SecureStore is not available on web and any stored installationID
      // would not be very reliable anyway
      const isSecureStoreAvailable = await SecureStore.isAvailableAsync();
      const storedInstallationID = isSecureStoreAvailable
        ? await SecureStore.getItemAsync(STORAGE_KEY)
        : null;

      installationID =
        storedInstallationID || (await _getOrCreateInstallationID());

      if (isSecureStoreAvailable && !storedInstallationID) {
        await SecureStore.setItemAsync(STORAGE_KEY, installationID);
      }
    } catch (e) {
      // Set an installationID as it's required for saving notification tokens
      installationID = await _getOrCreateInstallationID();
    }
  }

  return installationID;
};
